import { Inspection } from '@/types/Inspection.type';
import { FactoryVisitSession, LocationVerificationDraft } from '@/types/LocationVerification.type';
import {
  canAutoSkipFactoryVerification,
  evaluateFactoryVerificationCaptureDecision,
  inspectionRequiresLocationVerification,
} from '@/utils/locationVerification.utils';
import { DeviceLocation } from '@/utils/startInspectionGpsVerification.utils';

export type StartInspectionGpsResolutionAction =
  | { action: 'skip' }
  | { action: 'start'; captureRequired: false }
  | { action: 'start'; captureRequired: true }
  | { action: 'startWithReusedEvidence'; session: FactoryVisitSession };

export type ResolveStartInspectionGpsActionParams = {
  inspection: Inspection | null;
  factoryVerificationEnabled: boolean;
  draft: LocationVerificationDraft | null;
  factorySession: FactoryVisitSession | null;
  reuseDays: number;
  deviceLocation: DeviceLocation | null;
  distanceThresholdM: number;
};

/**
 * Resolves whether start should skip verification, reuse session evidence, or continue capture routing.
 * Reuse is only allowed when verification capture is required.
 */
export function resolveStartInspectionGpsAction(
  params: ResolveStartInspectionGpsActionParams
): StartInspectionGpsResolutionAction {
  if (!inspectionRequiresLocationVerification(params.inspection)) {
    return { action: 'skip' };
  }
  if (!params.factoryVerificationEnabled) {
    return { action: 'start', captureRequired: false };
  }
  const captureDecision = evaluateFactoryVerificationCaptureDecision({
    inspection: params.inspection,
    factoryVerificationEnabled: params.factoryVerificationEnabled,
    draft: params.draft,
    deviceLocation: params.deviceLocation,
    distanceThresholdM: params.distanceThresholdM,
  });
  if (!captureDecision.requiresCapture) {
    return { action: 'start', captureRequired: false };
  }
  if (
    params.factorySession &&
    canAutoSkipFactoryVerification({
      draft: params.draft,
      session: params.factorySession,
      reuseDays: params.reuseDays,
    })
  ) {
    return { action: 'startWithReusedEvidence', session: params.factorySession };
  }
  return { action: 'start', captureRequired: true };
}
